import React from "react";

import { useNotifications } from "@/queries/account";
import { getPushEnvironment, getPushPermission } from "@/lib/pushNotifications";

export function useNotificationAttention() {
  const { data, isLoading } = useNotifications();
  const [permission, setPermission] = React.useState(() =>
    typeof Notification === "undefined" ? "default" : Notification.permission
  );
  const [permissionReady, setPermissionReady] = React.useState(false);

  React.useEffect(() => {
    let disposed = false;
    const syncPermission = async () => {
      try {
        const nextPermission = await getPushPermission();
        if (!disposed) setPermission(nextPermission);
      } finally {
        if (!disposed) setPermissionReady(true);
      }
    };

    void syncPermission();
    document.addEventListener("visibilitychange", syncPermission);
    window.addEventListener("focus", syncPermission);
    window.addEventListener("push:permission-changed", syncPermission);

    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", syncPermission);
      window.removeEventListener("focus", syncPermission);
      window.removeEventListener("push:permission-changed", syncPermission);
    };
  }, []);

  const envOk = React.useMemo(() => getPushEnvironment(), []);
  const pushDemandedBySettings = React.useMemo(() => {
    const matchReminderEnabled = !!data?.push?.match_reminder?.enabled;
    const lineupUpdatesEnabled = !!data?.push?.lineup_updates?.enabled;
    const generalNewsEnabled = !!data?.push?.general_news?.enabled;
    return matchReminderEnabled || lineupUpdatesEnabled || generalNewsEnabled;
  }, [data]);

  const needsSystemPermission =
    permissionReady &&
    pushDemandedBySettings &&
    envOk.https &&
    envOk.sw &&
    envOk.push &&
    envOk.notif &&
    permission !== "granted";

  return {
    isLoading: isLoading || !permissionReady,
    permissionReady,
    permission,
    setPermission,
    envOk,
    pushDemandedBySettings,
    needsNotificationsAttention: needsSystemPermission,
  };
}
